import {
    Box,
    Grid,
    Stack,
    Table,
    TableBody,
    TableContainer,
    CircularProgress
} from '@mui/material';

import {
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    TableFooter,
    TableFooterCell
} from '@/components/shared/styles/Table';
import { Heading, SMBold } from '@/typography/Typography';
import { Dropdown } from '@/components/shared/Dropdown';
import { useState, useEffect } from 'react';
import { useFormatting } from '@/utils/formatting';
import { useTranslation } from 'react-i18next';
import { SearchGrid } from '@/components/SearchGrid';
import Chip from '@/components/shared/Chip';
import { LinearProgress } from '@mui/material';
import { DateRangeDropdown } from '@/components/shared/DateRangeDropdown';
import dayjs from 'dayjs';
import { TableCard } from '../shared/Card';
import CardHeader from '../shared/CardHeader';
import PaymentsAPIServiceV2 from '@/services/PaymentsServiceV2';
import styled from '@emotion/styled';

const StyledLinearProgress = styled(LinearProgress)`
    height: 8px;
    border-radius: 4px;
    background-color: transparent;
    width: 100%;
    max-width: 600px;

    & .MuiLinearProgress-bar {
        background-color: #1570ef;
        border-radius: 4px;
    }
`;

interface ChurnData {
    name: string | null;
    percentage: number;
    persons: number;
}

interface EmployeeChurnResponse {
    data: ChurnData[];
    total: ChurnData;
}

export const EmployeeChurnReport = () => {
    const [groupBy, setGroupBy] = useState<'department' | 'group'>(
        'department'
    );
    const [startDate, setStartDate] = useState<Date>(
        dayjs().startOf('month').toDate()
    );
    const [endDate, setEndDate] = useState<Date>(
        dayjs().endOf('month').toDate()
    );
    const [timeFilter, setTimeFilter] = useState('currentMonth');
    const [data, setData] = useState<EmployeeChurnResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const { toDecimal } = useFormatting();
    const { t } = useTranslation();

    const currentYear = new Date().getFullYear();

    const filterOptions = [
        { value: 'currentMonth', name: t('currentMonth') },
        { value: 'lastMonth', name: t('lastMonth') },
        { value: 'customMonth', name: t('customMonth') },
        { value: 'currentYear', name: t('currentYear', { year: currentYear }) },
        { value: 'lastYear', name: t('lastYear', { year: currentYear - 1 }) }
    ];

    const fetchData = async () => {
        setLoading(true);
        try {
            const response =
                await PaymentsAPIServiceV2.getInstance().getEmployeeChurnReport(
                    dayjs(startDate).format('YYYY-MM-DD'),
                    dayjs(endDate).format('YYYY-MM-DD'),
                    groupBy
                );
            setData(response.data);
        } catch (error) {
            console.error('Error fetching employee churn report:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [startDate, endDate, groupBy]);

    const filtereDateData = async (options: string) => {
        if (options === 'currentMonth') {
            setStartDate(dayjs().startOf('month').toDate());
            setEndDate(dayjs().endOf('month').toDate());
        } else if (options === 'lastMonth') {
            setStartDate(
                dayjs().subtract(1, 'month').startOf('month').toDate()
            );
            setEndDate(dayjs().subtract(1, 'month').endOf('month').toDate());
        } else if (options === 'currentYear') {
            setStartDate(dayjs().startOf('year').toDate());
            setEndDate(dayjs().endOf('year').toDate());
        } else if (options === 'lastYear') {
            setStartDate(dayjs().subtract(1, 'year').startOf('year').toDate());
            setEndDate(dayjs().subtract(1, 'year').endOf('year').toDate());
        }
    };

    const onMonthChange = (date: Date) => {
        setTimeFilter('customMonth');
        const startOfMonth = dayjs(date).startOf('month').toDate();
        const endOfMonth = dayjs(date).endOf('month').toDate();
        setStartDate(startOfMonth);
        setEndDate(endOfMonth);
    };

    const churnData = data?.data || [];

    const maxPercentage = Math.max(...churnData.map((d) => d.percentage), 1);

    const totalPercentage = data?.total?.percentage || 0;
    const totalPersons = data?.total?.persons || 0;

    return (
        <Box>
            <SearchGrid container justifyContent={'space-between'}>
                <Stack direction={'row'} alignItems={'center'} gap={1}>
                    <div>
                        <SMBold noWrap={true}>{t('filter_by')}:</SMBold>
                    </div>
                    <div>
                        <DateRangeDropdown
                            startDate={startDate}
                            endDate={endDate}
                            timeFilter={timeFilter}
                            setTimeFilter={setTimeFilter}
                            filterOptions={filterOptions}
                            filtereDateData={filtereDateData}
                            onMonthChange={onMonthChange}
                            showDatePicker={false}
                        />
                    </div>
                    <div style={{ width: '220px' }}>
                        <Dropdown
                            allowClear={false}
                            label={''}
                            choices={[
                                { label: 'Departments', value: 'department' },
                                { label: 'Groups', value: 'group' }
                            ]}
                            value={groupBy}
                            onChange={function (value: any | any[]): void {
                                setGroupBy(value as 'department' | 'group');
                            }}
                        />
                    </div>
                </Stack>
            </SearchGrid>
            <TableCard>
                <CardHeader label={t('employee_churn_report')}></CardHeader>
                <Box>
                    <TableContainer>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell width={'15%'}>
                                        {groupBy === 'group'
                                            ? t('group')
                                            : t('department')}
                                    </TableHeaderCell>
                                    <TableHeaderCell width={'55%'} />
                                    <TableHeaderCell
                                        width={'15%'}
                                        align="right"
                                    >
                                        {t('percentage')}
                                    </TableHeaderCell>
                                    <TableHeaderCell
                                        width={'15%'}
                                        align="right"
                                    >
                                        {t('persons')}
                                    </TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading && (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            <Box py={4}>
                                                <CircularProgress size={40} />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {!loading &&
                                    churnData.map((item, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                {item.name || '-'}
                                            </TableCell>
                                            <TableCell>
                                                <StyledLinearProgress
                                                    variant="determinate"
                                                    value={
                                                        (item.percentage /
                                                            maxPercentage) *
                                                        100
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                {toDecimal(item.percentage, 2)}%
                                            </TableCell>
                                            <TableCell align="right">
                                                {item.persons}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TableFooterCell>
                                        <SMBold>{t('total')}</SMBold>
                                    </TableFooterCell>
                                    <TableFooterCell />
                                    <TableFooterCell align="right"></TableFooterCell>
                                    <TableFooterCell align="right">
                                        <SMBold>{totalPersons}</SMBold>
                                    </TableFooterCell>
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                </Box>
            </TableCard>
        </Box>
    );
};
